import './styles.scss';

import { createNotification, handleError } from '@/utils/custom';
import { useEffect, useMemo } from 'react';

import { CategoriesHeader } from '@/features/Settings/pages/Categories/components/CategoriesHeader';
import { CategoriesList } from '@/features/Settings/pages/Categories/components/CategoriesList';
import { CategoriesTabs } from '@/features/Settings/pages/Categories/components/CategoriesTabs';
import CategoryFilters from '@/features/Settings/pages/Categories/components/CategoryFilters';
import CategoryModal from '@/features/Settings/pages/Categories/components/CategoryModal';
import { LoadingBlur } from '@/components/LoadingSpinner';
import PageInfo from '@/components/PageInfo';
import { Toast } from '@/models';
import { TrainingCategoriesTypes } from '@/features/Settings/pages/Categories/types';
import { createPersistentComponentState } from '@/state';
import { customIndexedDBStorage } from '@/state/storage';
import { deleteCategory } from '@/features/Settings/api/services/training/deleteQuestionnaireCategories';
import useConfirmation from '@/hooks/useConfirmation';

// The Dispatch role's displayName from the backend is "Dispatch", but the tab should read "Dispatcher".
const TRAINING_ROLES = [
  { name: 'Driver', label: 'Driver' },
  { name: 'Leader', label: 'Leader' },
  { name: 'Dispatch', label: 'Dispatcher' },
];

export const useCategoriesState = createPersistentComponentState<TrainingCategoriesTypes.State>(
  'TrainingCategoriesState',
  TrainingCategoriesTypes.initState,
  { storage: customIndexedDBStorage }
);

const TrainingCategories = () => {
  const [
    {
      categories,
      categoryGroups,
      roles,
      questionCounts,
      total,
      loading,
      roleId,
      modal,
      fetch,
      fetchRoles,
      fetchQuestionCounts,
      onSelectRole,
    },
    setState,
  ] = useCategoriesState(({ state, setState }) => [
    {
      categories: state.categories,
      categoryGroups: state.categoryGroups,
      roles: state.roles,
      questionCounts: state.questionCounts,
      total: state.total,
      loading: state.loading,
      roleId: state.filters.roleId,
      modal: state.modals.categoryModal,
      fetch: state.fetch,
      fetchRoles: state.fetchRoles,
      fetchQuestionCounts: state.fetchQuestionCounts,
      onSelectRole: state.onSelectRole,
    },
    setState,
  ]);
  const confirmDeleteCategory = useConfirmation({ Footer: { resolve: 'DELETE PERMANENTLY', reject: 'CANCEL' } });

  useEffect(() => {
    fetchRoles();
    fetchQuestionCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs = useMemo(
    () =>
      TRAINING_ROLES.map(({ name, label }) => {
        const role = roles.find((role) => role.displayName.toLowerCase() === name.toLowerCase());
        return role ? { ...role, displayName: label } : undefined;
      }).filter(Boolean),
    [roles]
  );

  useEffect(() => {
    if (!roleId && tabs.length > 0) onSelectRole(tabs[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, roleId]);

  const activeCategories = useMemo(() => categories.filter((category) => category.active === 1), [categories]);

  const openCreateModal = (): void => setState((current) => ({ ...current, modals: { ...current.modals, categoryModal: { show: true } } }));
  const openEditModal = (id: string): void =>
    setState((current) => ({
      ...current,
      modals: { ...current.modals, categoryModal: { show: true, category: categories.find((c) => c.id === id) } },
    }));
  const hideModal = (): void =>
    setState((current) => ({
      ...current,
      modals: { ...current.modals, categoryModal: { ...current.modals.categoryModal, show: false } },
    }));

  const handleDeleteCategory = async (id: string): Promise<void> => {
    const category = categories.find((c) => c.id === id);
    try {
      await confirmDeleteCategory({
        Body: {
          title: 'Delete Category',
          message: `Are you sure you want to delete "${category?.name}"? It will no longer be available for new evaluations.`,
        },
      });
    } catch {
      return;
    }
    try {
      await deleteCategory([id]);
      createNotification('Success', Toast.Type.SUCCESS, 'Delete Category');
      fetch();
    } catch (err) {
      handleError(err, { notification: { title: 'Delete Category' } });
    }
  };

  return (
    <div className="TrainingCategories">
      <CategoryFilters />
      <PageInfo>Items: {total}</PageInfo>
      <LoadingBlur loading={loading} />
      <CategoriesHeader onClick={openCreateModal} />
      <CategoriesTabs tabs={tabs} selectedRoleId={roleId} onSelect={onSelectRole} />
      <CategoriesList
        categories={activeCategories}
        questionCounts={questionCounts}
        categoryGroups={categoryGroups}
        onEdit={openEditModal}
        onDelete={handleDeleteCategory}
      />
      <CategoryModal
        show={modal?.show || false}
        category={modal?.category}
        groupLinks={categoryGroups?.[modal?.category?.id]}
        roleId={roleId}
        onHide={hideModal}
        onSubmit={fetch}
      />
    </div>
  );
};

export default TrainingCategories;
